import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Avatar, AvatarFallback } from "./ui/avatar";
import {
  Building2,
  LayoutDashboard,
  Home,
  CreditCard,
  Landmark,
  LogOut,
  User,
  Menu,
  X,
  Send,
  ShieldCheck,
} from "lucide-react";
import { useState } from "react";

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const vendorNavItems = [
    { path: "/vendor", label: "Dashboard", icon: LayoutDashboard },
    { path: "/projects", label: "Projects", icon: Home },
    { path: "/payments", label: "Payments", icon: CreditCard },
    { path: "/payouts", label: "Payouts", icon: Send },
    { path: "/bank-account", label: "Bank Account", icon: Landmark },
  ];

  const adminNavItems = [
    { path: "/admin/portal", label: "CFO Portal", icon: LayoutDashboard },
    { path: "/payouts", label: "Payouts", icon: Send },
    { path: "/bank-account", label: "Bank Account", icon: Landmark },
  ];

  const employeeNavItems = [
    { path: "/employee", label: "Dashboard", icon: LayoutDashboard },
    { path: "/payments", label: "Payments", icon: CreditCard },
    { path: "/bank-account", label: "Bank Account", icon: Landmark },
  ];

  const navItems = user?.role === "account_manager" ? adminNavItems : user?.role === "vendor" ? vendorNavItems : employeeNavItems;

  const getInitials = (name) => {
    return name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "U";
  };

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col">
      {/* Header */}
      <header className="glass-header fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to={user?.role === "account_manager" ? "/admin/portal" : user?.role === "vendor" ? "/vendor" : "/employee"} className="flex items-center">
              <img src="/cpc-logo.png" alt="Comprehensive Pain Consultants" className="h-9 w-auto rounded-lg bg-emerald-900 px-2.5 py-1.5" />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <Link key={item.path} to={item.path}>
                  <Button
                    variant={location.pathname === item.path ? "secondary" : "ghost"}
                    className={`gap-2 ${
                      location.pathname === item.path
                        ? "bg-emerald-100 text-emerald-900"
                        : "text-stone-600 hover:text-emerald-900"
                    }`}
                    data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </Button>
                </Link>
              ))}
            </nav>

            {/* User Menu */}
            <div className="flex items-center gap-4">
              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>

              {/* User Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2 px-2" data-testid="user-menu-btn">
                    <Avatar className="w-8 h-8">
                      <AvatarFallback className="bg-emerald-100 text-emerald-900 text-sm">
                        {getInitials(user?.full_name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:block text-sm font-medium text-stone-700">
                      {user?.full_name?.split(" ")[0]}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.full_name}</p>
                    <p className="text-xs text-stone-500">{user?.email}</p>
                    <p className="text-xs text-emerald-600 capitalize mt-1" data-testid="user-role-label">
                      {user?.role === "vendor" ? "Vendor" : user?.role === "employee" ? "Employee" : user?.role === "account_manager" ? "Account Manager" : user?.role}
                    </p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/settings/mfa")} className="cursor-pointer" data-testid="security-settings-btn">
                    <ShieldCheck className="w-4 h-4 mr-2" />
                    Security
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-stone-200 bg-white/95 backdrop-blur-md">
            <div className="px-4 py-3 space-y-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Button
                    variant={location.pathname === item.path ? "secondary" : "ghost"}
                    className={`w-full justify-start gap-2 ${
                      location.pathname === item.path
                        ? "bg-emerald-100 text-emerald-900"
                        : "text-stone-600"
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </Button>
                </Link>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="pt-20 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">{children}</div>
      </main>

      {/* Legal Footer */}
      <footer className="border-t border-stone-200 mt-auto py-6 px-4 sm:px-6 lg:px-8" data-testid="app-legal-footer">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-stone-500">
          <span>© {new Date().getFullYear()} Comprehensive Pain Consultants</span>
          <div className="flex items-center gap-5">
            <Link to="/terms" className="hover:text-emerald-700 transition-colors" data-testid="app-footer-terms-link">
              Terms
            </Link>
            <Link to="/privacy-policy" className="hover:text-emerald-700 transition-colors" data-testid="app-footer-privacy-link">
              Privacy Policy
            </Link>
            <Link to="/plaid-privacy" className="hover:text-emerald-700 transition-colors" data-testid="app-footer-plaid-privacy-link">
              Plaid Privacy
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
